/**
 * «Kayıt ve hareket geçmişi» — the record's own story, read from the server and drawn honestly.
 *
 * WHY THIS REPLACES THE PROTOTYPE'S CARD ONLY WHERE THERE IS A SERVER
 * -------------------------------------------------------------------
 * The prototype's «Denetim kaydı» card kept usage measurement in the tab and said so: «prototipte
 * oturumluk tutulur, sayfa yenilenince silinir». On that surface the sentence is TRUE — the visual
 * acceptance demo has no backend, and a card there claiming durable history would be claiming
 * something that does not exist. In the product it was false, which is what the Product Owner asked
 * to be fixed. So the product surface (a real session, a real backend) draws THIS card, and the
 * demo surface keeps the prototype's — which is also why the frozen reference is untouched and the
 * visual gate stays at `diffPixels = 0` with no new approved correction.
 *
 * WHAT THE SCREEN REFUSES TO SAY
 * ------------------------------
 * Every line's name comes from the shared vocabulary and is the level the act was observed at, and
 * the `caution` beside it says what the name does NOT prove. An interaction has no identified
 * actor and is drawn as «Anonim ziyaretçi» rather than attributed to the certificate's holder. A
 * section the role may not read says so, because «you may not see this» and «nothing happened» are
 * different answers. And the empty state is «Henüz kaydedilmiş hareket yok» — a fact about the
 * record, not about this browser tab.
 *
 * COUNTERS ARE OVER THE WHOLE HISTORY
 * -----------------------------------
 * The tab counts come from the server's totals, never from `entries.length`, so a counter never
 * says «50» because fifty happen to fit on the page.
 */
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import type { CredentialHistoryView } from '../data/commands';
import { useV07Commands } from '../data/commands-context';
import { useReadsRevision } from '../data/queries-context';
import {
  HISTORY_ACTOR_TR,
  HISTORY_CATEGORY_TR,
  HISTORY_EMPTY,
  HISTORY_EVIDENCE_TR,
  HISTORY_OUTCOME_TR,
  HISTORY_SOURCE_TR,
  historyCaution,
  historyLabel,
} from '../lib/credential-history';
import { fmtDateTime, fmtNum } from '../lib/reference';
import { useToast } from '../ui/toast';

type Tab = 'all' | 'system' | 'interaction';

const PAGE = 25;

const TAB_TR: Record<Tab, string> = {
  all: 'Tümü',
  system: HISTORY_CATEGORY_TR.system as string,
  interaction: HISTORY_CATEGORY_TR.interaction as string,
};

/** The timeline's own class, so a line is coloured by what it is rather than by how it reads. */
const dotClass = (outcome: string): string =>
  outcome === 'failed' ? 'revoked' : outcome === 'unknown' ? 'email_failed' : 'created';

export function CredentialHistoryCard({ code }: { code: string }): ReactElement {
  const commands = useV07Commands();
  const toast = useToast();
  /*
   * A command that changed the record changes its history too.
   *
   * Revoking a certificate writes a ledger row, and a card that had already read its history would
   * go on showing the state before the act — which is exactly the kind of screen this section
   * exists to replace. The reads revision is the signal every other read on this screen uses, so
   * this one uses it as well and re-reads when the world moves.
   */
  const revision = useReadsRevision();
  const [tab, setTab] = useState<Tab>('all');
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<CredentialHistoryView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (nextTab: Tab, nextOffset: number): Promise<void> => {
      setLoading(true);
      /*
       * A command layer without this read is not a crash, it is an unreadable history.
       *
       * The product's layer always has it; a surface mounted with a narrower one — a test harness,
       * an older build — would otherwise throw inside an effect and take the whole detail screen
       * down over a section of it. It says «okunamadı», which is what it is.
       */
      if (typeof commands.readCredentialHistory !== 'function') {
        setLoading(false);
        setError('Geçmiş okunamadı.');
        return;
      }
      const r = await commands.readCredentialHistory({
        code,
        category: nextTab,
        limit: PAGE,
        offset: nextOffset,
      });
      setLoading(false);
      if (r.ok && r.history) {
        setView(r.history);
        setError(null);
      } else setError(r.message ?? 'Geçmiş okunamadı.');
    },
    [commands, code],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: `revision` is not read inside `load`; it is the signal that the world changed under it, and dropping it from this list is what makes the card show the record's state from before the command — the defect the button contract caught
  useEffect(() => {
    void load(tab, offset);
  }, [load, tab, offset, revision]);

  const pick = (next: Tab): void => {
    setTab(next);
    /* A tab change starts at the beginning; keeping the offset would open on a blank page. */
    setOffset(0);
  };

  const download = async (): Promise<void> => {
    setBusy(true);
    const r = await commands.exportCredentialHistory({ code, category: tab });
    setBusy(false);
    if (!r.ok || !r.body) {
      toast(r.message ?? 'Dosya alınamadı', 'danger');
      return;
    }
    const blob = typeof r.body === 'string' ? new Blob([r.body], { type: 'text/csv' }) : r.body;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = r.filename ?? 'hpw-hareket-gecmisi.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totals = view?.totals ?? { all: 0, system: 0, interaction: 0 };
  const count = (t: Tab): number =>
    t === 'all' ? totals.all : t === 'system' ? totals.system : totals.interaction;
  const total = view?.page.total ?? 0;
  const shown = view?.entries.length ?? 0;

  return (
    <div className="card" id="cdHistory">
      <div className="card-head">
        <h2>Kayıt ve hareket geçmişi</h2>
        <span className="small muted">
          {view?.trackingStartedAt
            ? `hareket ölçümü ${fmtDateTime(view.trackingStartedAt)} tarihinde başladı`
            : 'hareket ölçümünün başlangıcı okunamadı'}
        </span>
      </div>
      <div className="tabs" id="cdHistoryTabs" role="tablist" style={{ padding: '0 18px' }}>
        {(['all', 'system', 'interaction'] as Tab[]).map((t) => (
          // biome-ignore lint/a11y/useValidAnchor: the reference's own tab strip markup
          <a
            key={t}
            href="#"
            data-histtab={t}
            role="tab"
            aria-selected={tab === t}
            className={tab === t ? 'is-active' : undefined}
            onClick={(e) => {
              e.preventDefault();
              pick(t);
            }}
          >
            {`${TAB_TR[t]} (${fmtNum(count(t))})`}
          </a>
        ))}
      </div>
      <div className="card-pad">
        {error ? <div className="help">{error}</div> : null}

        {/* Refused sections are named. An empty list would say something else entirely. */}
        {view && !view.access.system ? (
          <div className="help" id="cdHistoryNoSystem">
            {
              'Sistem işlemlerini görme yetkiniz yok; bu bölüm boş olduğu için değil, kapalı olduğu için görünmüyor.'
            }
          </div>
        ) : null}
        {view && !view.access.interactions ? (
          <div className="help" id="cdHistoryNoInteractions">
            {
              'Kullanıcı etkileşimlerini görme yetkiniz yok; bu bölüm boş olduğu için değil, kapalı olduğu için görünmüyor.'
            }
          </div>
        ) : null}

        {loading && !view ? <div className="help">Geçmiş okunuyor…</div> : null}

        {view && total === 0 && !loading ? (
          <p className="muted small" id="cdHistoryEmpty">
            {HISTORY_EMPTY}
          </p>
        ) : null}

        {view && total > 0 ? (
          <div className="timeline" id="cdHistoryList">
            {view.entries.map((e) => {
              const caution = historyCaution(e.event);
              return (
                <div className={`ev ${dotClass(e.outcome)}`} key={e.id} data-history-event={e.event}>
                  <span className="dot" />
                  <div>
                    <b>{historyLabel(e.event)}</b>
                    <span>
                      {[
                        fmtDateTime(e.at),
                        HISTORY_OUTCOME_TR[e.outcome] ?? e.outcome,
                        /* Who — and «Anonim ziyaretçi» where nobody was identified. */
                        e.actor.label ?? HISTORY_ACTOR_TR[e.actor.kind] ?? e.actor.kind,
                        HISTORY_SOURCE_TR[e.source] ?? e.source,
                        HISTORY_EVIDENCE_TR[e.evidence] ?? e.evidence,
                        ...(e.reason ? [`neden: ${e.reason}`] : []),
                      ].join(' · ')}
                      {e.correlationId ? ' · ' : null}
                      {e.correlationId ? <span className="mono small">{e.correlationId}</span> : null}
                    </span>
                    {caution ? (
                      <span className="muted" data-history-caution="">
                        {caution}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {view && total > PAGE ? (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}
            id="cdHistoryPager"
          >
            <button
              className="btn btn-sm"
              type="button"
              id="cdHistoryPrev"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(offset - PAGE, 0))}
            >
              Önceki
            </button>
            <button
              className="btn btn-sm"
              type="button"
              id="cdHistoryNext"
              disabled={offset + shown >= total || loading}
              onClick={() => setOffset(offset + PAGE)}
            >
              Sonraki
            </button>
            <span className="small muted">
              {`${fmtNum(offset + 1)}–${fmtNum(offset + shown)} / ${fmtNum(total)}`}
            </span>
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <button
            className="btn btn-sm"
            type="button"
            id="cdHistoryExport"
            disabled={busy || !view}
            onClick={() => void download()}
          >
            {busy ? 'Hazırlanıyor…' : 'Geçmişi CSV indir'}
          </button>
        </div>

        {/*
         * What this section is, without the two absolute characterisations the Product Owner
         * removed. It names the source, the durability and the limit of each kind of line rather
         * than declaring the whole card «hukuki kayıt» or «hukuki kayıt DEĞİLDİR».
         */}
        <p className="help" style={{ marginTop: '10px' }}>
          {
            'Sistem işlemleri, komutu yürüten uygulamanın append-only defterinden ve e-posta gönderim kayıtlarından gelir; kalıcıdır ve silinmez. Kullanıcı etkileşimleri tarayıcının bildirdiği ölçümlerdir: kalıcı olarak saklanır ve sayfa yenilendiğinde kaybolmaz, ancak ziyaretçinin kimliğini doğrulamaz ve her satır yalnız ölçülebilen adımı söyler. Bir satırın neyi kanıtlamadığı satırın altında yazar.'
          }
        </p>
      </div>
    </div>
  );
}
